import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { hairdresserService } from '../../_services/hairdresser.service';

/**
 * "How the money splits" (ONBOARDING-FIRST-RUN-PLAN.md, section 4).
 *
 * Shown on the payment screen when a self-employed stylist is on the bill,
 * until the owner dismisses it. It is the eureka for salons that run chair
 * renters or commission splits: the split happens at the till for Beu
 * payments, and everything else is tracked for settling in Commissions.
 */

const DISMISS_KEY = 'beu:split-card-dismissed';

// One staff lookup per page load, shared by every checkout.
let selfEmployedPromise = null;
function loadSelfEmployedIds() {
  if (!selfEmployedPromise) {
    selfEmployedPromise = hairdresserService
      .staffListServiceV2WithoutService({ hairdresserId: localStorage.getItem('userId'), page: 1, listType: 1 })
      .then((res) => {
        const list = (res && res.data && res.data.data && res.data.data.total) || [];
        return new Set(list
          .filter((s) => s.hairdresserInfo && s.hairdresserInfo.employmentTypeId === 2 && s.hairdresserInfo.isSalonOwner !== 1)
          .map((s) => String(s._id)));
      })
      .catch(() => { selfEmployedPromise = null; return new Set(); });
  }
  return selfEmployedPromise;
}

const readDismissed = () => { try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; } };

/** True when any line on the bill belongs to a self-employed stylist. */
export function useSelfEmployedOnBill(lineItems) {
  const [on, setOn] = useState(false);
  const staffKey = (lineItems || []).map((i) => i.staffId).filter(Boolean).join('|');
  useEffect(() => {
    if (!staffKey) { setOn(false); return undefined; }
    let live = true;
    loadSelfEmployedIds().then((ids) => {
      if (live) setOn(staffKey.split('|').some((id) => ids.has(String(id))));
    });
    return () => { live = false; };
  }, [staffKey]);
  return on;
}

export default function SplitAtTillCard({ visible, practice = false }) {
  const history = useHistory();
  const [dismissed, setDismissed] = useState(readDismissed);

  // Bailey's welcome always shows it (it is the point of that step), without
  // the close or a link that would leave the tour.
  if (!visible || (dismissed && !practice)) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <section className="co-split" aria-label="How the money splits">
      <header className="co-split-head">
        <span className="co-split-title">How the money splits</span>
        {!practice && (
          <button type="button" className="co-split-close" onClick={dismiss} aria-label="Got it, hide this">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        )}
      </header>
      <div className="co-split-row">
        <span className="co-split-mark co-split-mark--till" aria-hidden="true" />
        <p><b>Split at the till.</b> Card Reader, Tap to Pay, Payment Link, Klarna, Clearpay, Card on File and online payments. The stylist's share and the salon's share go to the right accounts as the client pays.</p>
      </div>
      <div className="co-split-row">
        <span className="co-split-mark" aria-hidden="true" />
        <p><b>Tracked for you.</b> Cash, other card machines and Other. Beu records who is owed what, and you settle up in Commissions.</p>
      </div>
      {!practice && <button type="button" className="co-split-link" onClick={() => history.push('/finance?tab=commissions')}>See Commissions</button>}
    </section>
  );
}
